import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { BandStateActions } from '../../shared/band-state/band-state.actions';
import {
  BandmanagerRestApiBandUpgrade,
  BandmanagerRestApiBandUpgradeStatusEnum,
  BandmanagerRestApiMyBandService,
  BandmanagerRestApiSettingBandroomUpgrade,
} from '@digitale-menschen/bandmanager-rest-api';
import { BandroomUpgradeInterface } from '../models/bandroom-upgrade.interface';
import dayjs from 'dayjs';
import { Store } from '@ngxs/store';
import { BandState } from '../../shared/band-state/band.state';
import { SettingsState } from '../../shared/settings-state/settings-state.state';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class BandroomService {
  private timerInterval = 60000;
  private timerRef: any = null;
  private referenceUpgrades: BandmanagerRestApiSettingBandroomUpgrade[] = [];
  private store = inject(Store);
  private apiMyBandService = inject(BandmanagerRestApiMyBandService);

  public startbandroomStatusUpdateInterval(): void {
    this.updateStatusesAndSaveInState();
    if (!this.timerRef) {
      this.timerRef = setInterval(() => {
        this.updateStatusesAndSaveInState();
      }, this.timerInterval);
    }
  }

  public async loadUpgrades(refreshFromApi = false): Promise<void> {
    if (!this.referenceUpgrades || this.referenceUpgrades.length === 0) {
      this.referenceUpgrades = await lastValueFrom(this.apiMyBandService.bandUpgradeControllerGetUpgradesReference());
    }
    let apiBandUpgrades: BandmanagerRestApiBandUpgrade[] = [];
    if (refreshFromApi) {
      const myBand = await lastValueFrom(this.apiMyBandService.bandControllerGetMyBand());
      apiBandUpgrades = myBand.upgrades || [];
    } else {
      apiBandUpgrades = this.store.selectSnapshot(BandState.band)?.upgrades || [];
    }
    const upgrades = this.prepareUpgradeItems(this.referenceUpgrades, apiBandUpgrades);
    this.updateStatusesAndSaveInState(upgrades);
    this.startbandroomStatusUpdateInterval();
  }

  public findSlowerUpgrade(upgrades: BandroomUpgradeInterface[]): BandroomUpgradeInterface | null {
    if (!upgrades || upgrades.length === 0) {
      return null;
    }
    const filteredUpgrades = upgrades.filter((item) => item.waitingTimeData !== undefined);
    if (filteredUpgrades.length === 0) {
      return null;
    }

    const sorted = filteredUpgrades.sort((a, b) =>
      (a.waitingTimeData?.waitingTimeTotalMin || 0) > (b.waitingTimeData?.waitingTimeTotalMin || 0) ? 1 : -1,
    );
    return sorted[sorted.length - 1];
  }

  public createWaitingTimeData(upgradeItem: BandroomUpgradeInterface): BandroomUpgradeInterface {
    const bandroomUpgradeDelays = this.store.selectSnapshot(SettingsState.settingByKey(environment.settingsKeys.bandroomUpgradeDelay)).hour;
    const targetDate = dayjs(upgradeItem.purchaseDate).add(bandroomUpgradeDelays, 'hour');
    const timeLeftToWaitMinutes = targetDate.diff(dayjs(), 'minute');
    if (timeLeftToWaitMinutes <= 0) {
      upgradeItem.status = BandmanagerRestApiBandUpgradeStatusEnum.Purchased;
      upgradeItem.waitingTimeData = undefined;
    } else {
      const waitingTimeHours = Math.floor(timeLeftToWaitMinutes / 60);
      upgradeItem.waitingTimeData = {
        waitingTimeTotalMin: timeLeftToWaitMinutes,
        waitingTimeHours: waitingTimeHours,
        waitingTimeMinutes: timeLeftToWaitMinutes - (60 * waitingTimeHours),
        progressPercent: 100 - (timeLeftToWaitMinutes / (bandroomUpgradeDelays * 60) * 100),
      };
    }
    return upgradeItem;
  }

  private prepareUpgradeItems(
    rawItems: BandmanagerRestApiSettingBandroomUpgrade[],
    apiUpgrades: BandmanagerRestApiBandUpgrade[],
  ): BandroomUpgradeInterface[] {
    const results: BandroomUpgradeInterface[] = [];
    for (let i = 0; i < rawItems.length; i++) {
      const newItem = JSON.parse(JSON.stringify(rawItems[i])) as BandroomUpgradeInterface;
      const bandUpgrade = apiUpgrades.find((item) => item.name === rawItems[i].purchasetype);
      if (bandUpgrade) {
        newItem.purchaseDate = bandUpgrade.purchaseDate;
        newItem.status = bandUpgrade.status;
      } else {
        newItem.status = BandmanagerRestApiBandUpgradeStatusEnum.Available;
      }
      if (newItem.purchaseDate && newItem.status !== BandmanagerRestApiBandUpgradeStatusEnum.Purchased) {
        const bandroomUpgradeDelays = this.store.selectSnapshot(
          SettingsState.settingByKey(environment.settingsKeys.bandroomUpgradeDelay)).hour;
        const dayJsPurchaseDate = dayjs(newItem.purchaseDate);
        const targetDate = dayJsPurchaseDate.add(bandroomUpgradeDelays, 'hour');
        if (dayjs().isAfter(targetDate)) {
          newItem.status = BandmanagerRestApiBandUpgradeStatusEnum.Purchased;
        } else {
          newItem.status = BandmanagerRestApiBandUpgradeStatusEnum.InProgress;
        }
      }
      results.push(newItem);
    }
    return results;
  }

  private updateStatusesAndSaveInState(upgradesArr?: BandroomUpgradeInterface[] | null): void {
    let upgrades = upgradesArr;
    if (!upgrades || upgrades.length === 0) {
      upgrades = this.store.selectSnapshot(BandState.bandroomUgrades);
    }
    if (!upgrades || upgrades.length === 0 || upgrades.findIndex(
      (item) => item.status === BandmanagerRestApiBandUpgradeStatusEnum.InProgress) === -1) {
      this.store.dispatch(new BandStateActions.SetBandroomUpgrades(upgrades || []));
      return;
    }

    for (let i = 0; i < upgrades.length; i++) {
      if (upgrades[i].status === BandmanagerRestApiBandUpgradeStatusEnum.InProgress) {
        upgrades[i] = this.createWaitingTimeData(upgrades[i]);
      }
    }

    this.store.dispatch(new BandStateActions.SetBandroomUpgrades(upgrades));
    return;
  }
}
